<template>
  <div class="download">
    <div class="hero-glow" aria-hidden="true"></div>
    <div class="lp-container download-inner">
      <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('hero.badge') }}</div>
      <h1 class="lp-h1 download-title lp-balance">{{ $t('download.title') }}</h1>
      <p class="lp-lead download-lead lp-balance">{{ $t('download.subtitle') }}</p>

      <!-- Descarga recomendada para el sistema detectado -->
      <div v-if="recommended" class="hero-actions">
        <a class="lp-btn lp-btn--primary lp-btn--lg" :href="hrefFor(recommended)" download>
          <l-icon small color="white">mdi-download</l-icon>
          {{ $t('download.ctaFor', { platform: label(recommended) }) }}
        </a>
        <a class="lp-btn lp-btn--ghost lp-btn--lg" :href="webAppUrl">
          <l-icon small>globe</l-icon>
          {{ $t('download.webCta') }}
        </a>
      </div>

      <!-- En móvil no hay app de escritorio que ofrecer: la web es la respuesta -->
      <div v-else class="hero-actions">
        <a class="lp-btn lp-btn--primary lp-btn--lg" :href="webAppUrl">
          <l-icon small color="white">globe</l-icon>
          {{ $t('download.webCta') }}
        </a>
      </div>

      <p class="download-meta">
        <template v-if="os === 'mobile'">{{ $t('download.mobileNote') }}</template>
        <template v-else>{{ $t('download.requirements') }}</template>
        <template v-if="version"> · {{ $t('download.version') }} {{ version }}</template>
      </p>

      <!-- Todas las plataformas -->
      <section class="platforms">
        <h2 class="platforms-title">{{ $t('download.allPlatforms') }}</h2>
        <div class="platforms-grid">
          <a
            v-for="build in orderedBuilds"
            :key="build.id"
            class="platform-card"
            :class="{ 'platform-card--mine': build.os === os }"
            :href="hrefFor(build)"
            download
          >
            <div class="platform-icon"><l-icon size="20" color="primary">{{ build.icon }}</l-icon></div>
            <div class="platform-copy">
              <span class="platform-name">{{ label(build) }}</span>
              <span class="platform-hint">{{ hint(build) }}</span>
            </div>
            <span class="platform-ext">{{ sizeOf(build) || build.ext }}</span>
          </a>
        </div>
      </section>

      <div class="download-shot lp-frame">
        <app-screenshot variant="inbox" :label="$t('download.title')"></app-screenshot>
      </div>

      <div class="download-note">
        <l-icon size="18" color="primary">mdi-information-outline</l-icon>
        <span>{{ $t('download.afterDownload') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import AppScreenshot from '@/components/mockup/AppScreenshot.vue';
import {
  BUILDS,
  WEB_APP_URL,
  APP_VERSION,
  detectOS,
  isAppleSilicon,
  downloadUrl,
  fetchLatestRelease,
  formatSize,
} from '@/constants/download';

export default {
  name: 'DownloadPage',
  components: { AppScreenshot },
  metaInfo() {
    return {
      title: this.$t('download.title'),
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.$t('download.subtitle') },
      ],
    };
  },
  data() {
    return {
      os: 'mac',
      builds: BUILDS,
      webAppUrl: WEB_APP_URL,
      // Tamaños de latest.json. Vacío hasta que llega (y para siempre si no se
      // puede leer): solo afecta a la etiqueta, los enlaces funcionan igual.
      assets: {},
      version: APP_VERSION,
    };
  },
  computed: {
    /**
     * Solo los binarios que existen de verdad. Mientras una plataforma no se
     * haya publicado, ofrecerla llevaría a un 404 con cara de descarga rota;
     * quien entre desde ella verá el CTA de la app web, que sí funciona.
     *
     * Si el manifiesto no se puede leer (`assets` vacío) enseñamos todos: es
     * preferible un enlace que quizá falle a una página de descargas vacía.
     */
    availableBuilds() {
      if (!Object.keys(this.assets).length) return this.builds;
      return this.builds.filter((b) => this.assets[b.id]);
    },
    /** Binario principal del sistema detectado (null si no hay ninguno). */
    recommended() {
      if (this.os === 'mobile') return null;
      const mine = this.availableBuilds.filter((b) => b.os === this.os);
      if (!mine.length) return null;
      if (this.os === 'mac' && !isAppleSilicon()) {
        return mine.find((b) => b.id === 'mac-intel') || mine[0];
      }
      return mine.find((b) => b.primary) || mine[0];
    },
    /** El sistema del visitante primero; el resto después, en el orden de BUILDS. */
    orderedBuilds() {
      const mine = this.availableBuilds.filter((b) => b.os === this.os);
      const rest = this.availableBuilds.filter((b) => b.os !== this.os);
      return [...mine, ...rest];
    },
  },
  created() {
    this.os = detectOS();
  },
  async mounted() {
    const release = await fetchLatestRelease();
    if (!release) return;
    this.assets = release.files;
    if (release.version) this.version = release.version;
  },
  methods: {
    label(build) {
      return this.$t(`${build.labelKey}.name`);
    },
    hint(build) {
      return this.$t(`${build.labelKey}.hint`);
    },
    /** Nombre estable en el servidor: no depende de haber leído el manifiesto. */
    hrefFor(build) {
      return downloadUrl(build);
    },
    sizeOf(build) {
      const asset = this.assets[build.id];
      return asset ? formatSize(asset.size) : '';
    },
  },
};
</script>

<style scoped>
.download {
  position: relative;
  min-height: 100vh;
  background: var(--lp-bg);
  overflow: hidden;
  /* 64px del AppBar fijo + aire, como las páginas legales */
  padding-top: 128px;
}
.hero-glow {
  position: absolute;
  top: -160px; left: 50%;
  transform: translateX(-50%);
  width: 1000px; height: 520px;
  background: radial-gradient(60% 60% at 50% 0%, var(--lp-accent-weak) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
}
.download-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 96px;
}
/* Sin regla propia los dos CTA quedaban pegados: aquí no llega la .hero-actions
   de HomePage, que es scoped. */
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}
.download-title { margin: 22px 0 0; }
.download-lead { max-width: 540px; margin: 22px 0 34px; }
.download-meta { margin-top: 18px; font-size: 0.875rem; color: var(--lp-muted); }

/* ---- Rejilla de plataformas ---- */
.platforms {
  margin-top: 56px;
  width: 100%;
  max-width: 860px;
  text-align: left;
}
.platforms-title {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--lp-muted);
  margin: 0 0 18px;
}
.platforms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}
.platform-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--lp-line);
  border-radius: 12px;
  background: var(--lp-bg-subtle);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.platform-card:hover {
  border-color: var(--lp-accent);
  transform: translateY(-1px);
}
.platform-card--mine { border-color: var(--lp-accent); }
.platform-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
}
.platform-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.platform-name { font-size: 0.9375rem; font-weight: 600; color: var(--lp-ink); }
.platform-hint { font-size: 0.8125rem; color: var(--lp-muted); }
.platform-ext {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--lp-muted);
  white-space: nowrap;
}

.download-shot { margin-top: 56px; width: 100%; max-width: 920px; }
.download-note {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 32px;
  padding: 14px 18px;
  border: 1px solid var(--lp-line);
  border-radius: 12px;
  background: var(--lp-bg-subtle);
  font-size: 0.95rem;
  color: var(--lp-ink-2);
  max-width: 620px;
  text-align: left;
}

@media (max-width: 599px) {
  .download { padding-top: 104px; }
  .platforms { margin-top: 40px; }
  .download-shot { margin-top: 40px; }
}
</style>
